/**
 * Shared session-state flags (module-level singletons, reset on page reload).
 *
 * Three independent guards coordinate session teardown across subsystems:
 *
 * 1. _isSessionInvalidatedHandled — "session-kill" lock.
 *    Claimed by whichever subsystem (Axios interceptor OR WebSocket handler)
 *    first detects a server-side session kill (e.g., login from another device).
 *    Ensures cleanup + overlay runs exactly once.
 *
 * 2. _isLoggingOut — intentional-logout flag.
 *    Set during user-initiated logout so that the Axios interceptor suppresses
 *    session-invalidation handling for the logout request itself.
 *
 * 3. _isExpiredRedirectInProgress — redirect-dedup lock for token expiry.
 *    When multiple 401s arrive simultaneously (e.g., returning from RegTank),
 *    only the first caller that claims this flag performs the redirect to /login.
 *    Subsequent callers become no-ops, preventing repeated page reloads that
 *    would destroy the session-expired overlay.
 *
 * All flags automatically reset on full page reload (window.location navigation).
 */

let _isSessionInvalidatedHandled = false;
let _isLoggingOut = false;

/**
 * Returns true if session invalidation cleanup is already in progress
 * (either from the Axios interceptor or the WebSocket handler).
 */
export const isSessionInvalidationInProgress = (): boolean =>
  _isSessionInvalidatedHandled;

/**
 * Claim the session invalidation lock.
 * Returns true if this caller won the lock, false if someone else already has it.
 */
export const claimSessionInvalidation = (): boolean => {
  if (_isSessionInvalidatedHandled) return false;
  _isSessionInvalidatedHandled = true;
  return true;
};

/**
 * Release the lock (e.g. after the user dismisses the overlay and navigates to /login).
 */
export const resetSessionInvalidation = (): void => {
  _isSessionInvalidatedHandled = false;
};

/**
 * Flag that an intentional logout is in progress.
 * The interceptor should suppress session-invalidation handling during this time.
 */
export const setLoggingOut = (value: boolean): void => {
  _isLoggingOut = value;
};

export const isLoggingOut = (): boolean => _isLoggingOut;

/**
 * Guard against multiple simultaneous redirects to /login on token expiry.
 * When multiple 401s fire at once (e.g., returning from external portal like RegTank),
 * only the first one should redirect — subsequent ones are no-ops.
 * This prevents the login page from reloading and destroying the session expired overlay.
 */
let _isExpiredRedirectInProgress = false;

export const claimExpiredRedirect = (): boolean => {
  if (_isExpiredRedirectInProgress) return false;
  _isExpiredRedirectInProgress = true;
  return true;
};
